/**
 * What the cancellation conversation is ABOUT.
 *
 * The chat is opened from a single product row, but most levers are scoped to
 * the whole subscription contract: delaying, spacing out deliveries, and
 * cancelling all hit every line on it. Copy that names one product is therefore
 * wrong the moment a customer has two — and combine-subscriptions is
 * deliberately creating more multi-line contracts.
 *
 * So every customer-facing string names the SUBJECT, not the clicked product.
 * The one exception is the swap offer, which really does only touch the single
 * line it names, and correctly keeps saying that product's title.
 *
 * Shared because the widget writes the greeting and the card labels while the
 * Next.js backend writes the system prompt — and they must not disagree about
 * what the customer is cancelling.
 */

export interface CancelSubject {
  /** Noun phrase naming what is being cancelled. */
  text: string;
  /** Object pronoun for card labels: "it" | "them" | "your deliveries". */
  pronoun: string;
  /** Number of product lines on the contract (>= 1). */
  lineCount: number;
}

const SUBSCRIPTION: CancelSubject = {
  text: "your subscription",
  pronoun: "your deliveries",
  lineCount: 1,
};

export function buildCancelSubject(
  productTitle: string,
  siblingLineTitles: readonly string[] = [],
): CancelSubject {
  const title = productTitle.trim();

  const siblings = [
    ...new Set(siblingLineTitles.map((t) => t.trim()).filter(Boolean)),
  ].filter((t) => t !== title);
  const lineCount = siblings.length + 1;

  // No title to speak of — never render "what drew you to  to begin with?".
  // The clicked line still counts, plus every deduped, non-blank sibling —
  // an empty title must not make a multi-line contract look single-line.
  if (!title) return { ...SUBSCRIPTION, lineCount };

  if (lineCount === 1) return { text: title, pronoun: "it", lineCount };
  if (lineCount === 2) {
    return {
      text: `${title} and ${siblings[0]}`,
      pronoun: "them",
      lineCount,
    };
  }
  return { ...SUBSCRIPTION, lineCount };
}
